import {
  ChangeDetectionStrategy,
  Component,
  Input,
  signal,
  computed,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { TextareaModule } from 'primeng/textarea';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

import { AddFormComponent } from '@shared/components/add-form/add-form.component';
import {
  AddPropertyDialogComponent,
  PropertyFormData,
} from '@app/feature/center-business/goods/spu/components/add-property-dialog/add-property-dialog.component';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  findCategoryOptionInTree,
  extractCategoryId,
  extractCategoryIdFromTreeselect,
  filterOutCurrentCategory,
  hasCategoryOptionsChanged,
  TreeNode,
} from '@shared/utils/treeselect.util';
import {
  SpuFormConfig,
  getTypeLabel,
  getSubmissionsTypeLabel,
  TYPES,
} from '../../spu.config';
import {
  SpuApiService,
  SpuFaqVO,
  SpuFaqUpsertDTO,
} from '../../services/spu-api.service';
import { SpuService } from '../../services/spu.service';
import { SpuMaterialService } from '../../services/spu-material.service';
import {
  SpuCategoryService,
  CategoryOption,
} from '../../services/spu-category.service';
import { SpuBrandService } from '../../services/spu-brand.service';
import { PaginatedDropdownController } from '@shared/utils/paginated-dropdown.util';
import { TaxCategoryService } from '@app/feature/configuration/product/tax-category/services/tax-category.service';
import { SerialProfileApiService } from '../../services/serial-profile-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { PermissionService } from '@app/core/services/permission.service';
import {
  CategoryAttributeVO,
  GoodsSkuControllerService,
  ProductFieldVO,
  ProductImageVO,
  SpuImageAltUpdateDTO,
} from '@app/api/goods-service';
import {
  ProductTaxControlComponent,
} from '../../../shared/product-tax/product-tax-control.component';
import {
  ProductTaxInheritedValue,
  ProductTaxOption,
  ProductTaxValue,
} from '../../../shared/product-tax/product-tax.model';
import {
  DEFAULT_PRODUCT_TAX_VALUE,
  deriveProductTaxMode,
  normalizeProductTaxSubmit,
} from '../../../shared/product-tax/product-tax.util';
import { FaqEditItem } from '../../../shared/spu-faq-editor/spu-faq.model';

/** `ProductImageVO` extended with a locally-editable alt text draft. */
interface ImageAltEditItem extends ProductImageVO {
  editedAltText: string;
}

/** Display-ready image alt item with a resolved label index for detail images. */
interface ImageAltDisplayItem extends ImageAltEditItem {
  isMain: boolean;
  detailIndex: number;
}

const CATEGORY_FORM_TYPES = {
  RADIO_BUTTON: 1,
  CHECKBOX: 2,
  TEXT_BOX: 3,
  DROPDOWN: 4,
} as const;

const SPU_SUBMISSIONS_TYPES = {
  RADIO_BUTTON: 0,
  CHECKBOX: 1,
  TEXT_BOX: 2,
  DROPDOWN: 3,
} as const;

/** Maps SPU form `controlName`s to their `ProductFieldVO.code` for field-permission gating. */
const CONTROL_NAME_TO_FIELD_CODE: Record<string, string> = {
  mainImage: 'main_image',
  usageVideo: 'video',
};

@Component({
  selector: 'app-spu-form-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    CheckboxModule,
    TextareaModule,
    ToastModule,
    AddFormComponent,
    AddPropertyDialogComponent,
    ConfirmationDialogComponent,
    ProductTaxControlComponent,
  ],
  providers: [MessageService],
  templateUrl: './spu-form-wrapper.component.html',
  styleUrls: ['./spu-form-wrapper.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpuFormWrapperComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() config!: SpuFormConfig;
  @Input() editMode = false;
  @Input() viewMode = false;
  /** FAQ section is opt-in: callers must explicitly pass true to show it. */
  @Input() showFaq = false;
  @Input() initialData: any = null;

  @ViewChild(AddFormComponent) set addFormComponent(value: AddFormComponent) {
    this._addFormComponent = value;
    if (value) {
      this.onAddFormComponentReady();
    }
  }
  get addFormComponent(): AddFormComponent {
    return this._addFormComponent!;
  }
  private _addFormComponent!: AddFormComponent;

  // Flag to track if we need to update images after view init
  private readonly pendingImageUpdates = {
    mainImage: null as any,
    detailImage: null as any,
  };

  // Store material name for loading images after view init
  private pendingMaterialName: string | null = null;

  showAddPropertyDialog = signal(false);
  showViewPropertyDialog = signal(false);
  editingProperty = signal<any>(null);
  viewingProperty = signal<any>(null);

  // Property table data management
  propertyTableData = signal<any[]>([]);
  nextPropertyId = signal(1);
  selectedProperties = signal<any[]>([]);

  // Confirmation dialog state
  showConfirmationDialog = signal(false);
  confirmationMessage = signal('');
  confirmationButtons = signal<any[]>([]);

  // Loading state for API calls
  isSaving = signal(false);
  private skipCanDeactivate = false;

  private readonly hasManualChanges = signal(false);

  // Form data management
  formData = signal<any>({});
  mainImageData = signal<any>(null);
  detailedImageData = signal<any>(null);
  usageVideoData = signal<any>(null);
  selectedAssetData = signal<any[]>([]); // Store detail images of selected product asset

  // RBA-2278: SPU image alt text (SEO). Loaded from GET /v1/goods/manager/{spuId}/images
  // and saved via PUT /v1/goods/manager/{spuId}/images/alt on SPU save (edit mode only).
  productImages = signal<ImageAltEditItem[]>([]);
  isSavingImageAlt = signal(false);

  // Display-ready image alt items with resolved "Main image" / "Detail image N" labels.
  imageAltDisplayItems = computed<ImageAltDisplayItem[]>(() => {
    let detailCounter = 0;
    return this.productImages().map((image) => {
      const isMain = image.role === 'MAIN';
      if (!isMain) {
        detailCounter += 1;
      }
      return { ...image, isMain, detailIndex: detailCounter };
    });
  });

  faqItems = signal<FaqEditItem[]>([]);
  private faqTempIdCounter = 0;
  // true once loadSpuFaqs has confirmed the current state from the server;
  // guards saveFaqsIfNeeded so a failed load can never be mistaken for "no FAQs".
  private faqsLoaded = false;
  // Last-loaded count, so saveFaqsIfNeeded can skip PUTting [] on unrelated edits.
  private initialFaqCount = 0;

  // Track if we're in the initial data loading phase
  private isInitialDataLoading = false;
  private selectedCategoryTemplateId: number | null = null;

  // Non-signal property for detailImages to prevent continuous updates
  detailImagesData: any[] = [];

  private readonly materialDropdownRevision = signal(0);
  private readonly materialDropdown = new PaginatedDropdownController({
    pageSize: 100,
    loadPage: ({ page, size, searchText }) =>
      this.materialService.getMaterialOptionsPage({
        materialName: searchText || undefined,
        page,
        size,
      }),
    getCurrentValue: () => this.formData()?.materialName,
    onOptionsChange: () =>
      this.materialDropdownRevision.update((value) => value + 1),
  });

  // Category options for dropdown
  categoryOptions = signal<CategoryOption[]>([]);

  // Category tree options for treeselect
  categoryTreeOptions = signal<any[]>([]);

  private readonly brandDropdownRevision = signal(0);
  private readonly brandDropdown = new PaginatedDropdownController({
    pageSize: 100,
    loadPage: ({ page, size, searchText }) =>
      this.brandService.getBrandOptionsPage({
        brandName: searchText || undefined,
        status: true,
        page,
        size,
        valueKey: 'name',
      }),
    getCurrentValue: () => this.formData()?.brandName,
    onOptionsChange: () =>
      this.brandDropdownRevision.update((value) => value + 1),
  });

  // Field metadata for field-level permission gating (see PermissionService.canEditField)
  productFields = signal<ProductFieldVO[]>([]);

  // Serial Profile options for dropdown
  serialProfileOptions = signal<{ label: string; value: number }[]>([]);
  taxOptions = signal<ProductTaxOption[]>([]);
  taxValue = signal<ProductTaxValue>(DEFAULT_PRODUCT_TAX_VALUE);
  inheritedTaxValue = signal<ProductTaxInheritedValue | null>(null);

  // Store original serialProfileId to detect changes
  private originalSerialProfileId: number | undefined;

  // Store SPU ID from route
  private spuId = signal<number | null>(null);

  // Store the form config with tree options to prevent re-renders
  private formConfigWithTreeOptions: any = null;

  // Updated form configuration with material, category, and brand options
  updatedFormConfig = computed(() => {
    this.materialDropdownRevision();
    this.brandDropdownRevision();
    if (!this.config) return this.config;
    // Only update if category options have changed or config is null
    if (
      !this.formConfigWithTreeOptions ||
      this.formConfigWithTreeOptions._lastCategoryOptionsLength !==
        this.categoryTreeOptions().length ||
      this.formConfigWithTreeOptions._lastMaterialDropdownRevision !==
        this.materialDropdownRevision() ||
      this.formConfigWithTreeOptions._lastFormDataMaterialName !==
        this.formData()?.materialName ||
      this.formConfigWithTreeOptions._lastFormDataCategoryName !==
        extractCategoryId(this.formData()?.categoryName) ||
      this.formConfigWithTreeOptions._lastBrandDropdownRevision !==
        this.brandDropdownRevision() ||
      this.formConfigWithTreeOptions._lastSerialProfileOptionsLength !==
        this.serialProfileOptions().length ||
      this.formConfigWithTreeOptions._lastFormDataBrandName !==
        this.formData()?.brandName ||
      this.formConfigWithTreeOptions._lastFormDataBrandId !==
        this.formData()?.brandId ||
      this.formConfigWithTreeOptions._lastIsSaving !== this.isSaving() ||
      this.formConfigWithTreeOptions._lastPimApprove !== this.pim_approve() ||
      this.formConfigWithTreeOptions._lastProductFieldsLength !==
        this.productFields().length
    ) {
      const updatedConfig = { ...this.config };
      updatedConfig.formFields = this.config.formFields.map((field) => {
        if (field.controlName === 'materialName') {
          return {
            ...field,
            type: 'searchableDropdown',
            ...this.materialDropdown.toSearchableDropdownProps(),
          };
        }
        if (field.controlName === 'categoryName') {
          const updatedField = {
            ...field,
            treeOptions: this.categoryTreeOptions(),
          };
          return updatedField;
        }
        if (field.controlName === 'additionalCategoryIds') {
          return {
            ...field,
            treeOptions: this.additionalCategoryTreeOptions(),
          };
        }
        if (field.controlName === 'brandName') {
          return {
            ...field,
            type: 'searchableDropdown',
            ...this.brandDropdown.toSearchableDropdownProps(),
          };
        }
        if (field.controlName === 'serialProfileId') {
          return {
            ...field,
            type: 'searchableDropdown',
            options: this.serialProfileOptions(),
            filter: true,
          };
        }
        if (field.controlName === 'status') {
          // Disable status checkbox in view mode OR if user doesn't have P_PIM.APPROVE permission
          // Enable in create/edit mode when user has approve permission
          const updatedField = {
            ...field,
            disabled: this.viewMode || !this.pim_approve(),
          };
          return updatedField;
        }
        const gatedFieldCode = CONTROL_NAME_TO_FIELD_CODE[field.controlName];
        if (gatedFieldCode) {
          const requiredPermission = this.productFields().find(
            (productField) => productField.code === gatedFieldCode,
          )?.requiredPermission;
          return {
            ...field,
            disabled: this.viewMode || !this.canEditField(requiredPermission),
          };
        }
        return field;
      });

      // Update header actions with loading state
      if (updatedConfig.headerActions) {
        updatedConfig.headerActions = updatedConfig.headerActions.map(
          (action: any) => {
            if (action.action === 'save') {
              return {
                ...action,
                loading: this.isSaving(),
                disabled: this.isSaving(),
              };
            }
            return action;
          },
        );
      }

      // Store the config with markers to track when it was last updated
      this.formConfigWithTreeOptions = {
        ...updatedConfig,
        _lastCategoryOptionsLength: this.categoryTreeOptions().length,
        _lastMaterialDropdownRevision: this.materialDropdownRevision(),
        _lastFormDataMaterialName: this.formData()?.materialName,
        _lastFormDataCategoryName: extractCategoryId(this.formData()?.categoryName),
        _lastBrandDropdownRevision: this.brandDropdownRevision(),
        _lastSerialProfileOptionsLength: this.serialProfileOptions().length,
        _lastFormDataBrandName: this.formData()?.brandName,
        _lastFormDataBrandId: this.formData()?.brandId,
        _lastIsSaving: this.isSaving(),
        _lastPimApprove: this.pim_approve(),
        _lastProductFieldsLength: this.productFields().length,
      };
    }

    return this.formConfigWithTreeOptions;
  });

  // Computed property to combine all form data for initialData
  combinedInitialData = computed(() => {
    const baseData = {
      ...this.formData(),
      mainImage: this.mainImageData(),
      detailedImage: this.detailedImageData(),
      usageVideo: this.usageVideoData(),
    };

    return baseData;
  });

  // Computed property to get updated table sections with current data
  updatedTableSections = computed(() => {
    if (!this.config.tableSections) return [];

    const propertyTableData = this.propertyTableData();
    const selectedProperties = this.selectedProperties();

    return this.config.tableSections.map((section) => {
      // Check for property table section by title (both hardcoded and i18n key)
      if (
        section.title === 'Property Table' ||
        section.title === 'goods.spu.attributes.property_table'
      ) {
        return {
          ...section,
          data: propertyTableData,
          selection: selectedProperties,
          actions: section.actions?.map((action: any) => {
            if (action.action === 'deleteSelected') {
              return {
                ...action,
                disabled: selectedProperties.length === 0,
              };
            }
            return action;
          }),
        };
      }
      return section;
    });
  });

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  pim_approve = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.product.approve');
  });

  canEditField(requiredPermission: string | null | undefined): boolean {
    return this.permissionService.canEditField(requiredPermission);
  }

  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly spuApiService: SpuApiService,
    private readonly spuService: SpuService,
    private readonly materialService: SpuMaterialService,
    private readonly categoryService: SpuCategoryService,
    private readonly brandService: SpuBrandService,
    private readonly serialProfileApiService: SerialProfileApiService,
    private readonly taxCategoryService: TaxCategoryService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly goodsSkuControllerService: GoodsSkuControllerService,
  ) {}

  ngOnInit(): void {
    this.setSpuServiceType();

    // Get SPU ID from route
    const routeSpuId = this.route.snapshot.queryParams['id'];
    if (routeSpuId) {
      this.spuId.set(parseInt(routeSpuId));
    }

    // Load material options
    this.materialDropdown.load({ reset: true });

    // Load category options
    this.loadCategoryOptions();

    // Load category tree options
    this.loadCategoryTreeOptions();

    // Load brand options
    this.brandDropdown.load({ reset: true });

    // Load serial policy options
    this.loadSerialPolicyOptions();

    this.loadTaxOptions();

    // Load field metadata for field-level permission gating
    this.loadProductFields();

    // Load existing data if in edit mode
    if (this.editMode || this.viewMode) {
      this.loadExistingData();
    }
  }

  ngAfterViewInit(): void {
    // Process any pending image updates that couldn't be processed during ngOnInit
    if (this.pendingImageUpdates.mainImage) {
      this.addFormComponent.updateMainImagesFromMaterial(
        this.pendingImageUpdates.mainImage,
      );
      this.pendingImageUpdates.mainImage = null;
    }
    if (this.pendingImageUpdates.detailImage) {
      this.addFormComponent.updateDetailedImagesFromMaterial(
        this.pendingImageUpdates.detailImage,
      );
      this.pendingImageUpdates.detailImage = null;
    }

    // Load material images if we have a pending material name
    if (this.pendingMaterialName) {
      this.loadMaterialImages(this.pendingMaterialName);
      this.pendingMaterialName = null;
    }

    // Initialize selected asset data if there's already a material name
    const currentMaterialName = this.formData()?.materialName;
    if (currentMaterialName) {
      this.updateSelectedAssetData(currentMaterialName);
    }
  }

  ngOnDestroy(): void {
    this.materialDropdown.destroy();
    this.brandDropdown.destroy();
  }

  // Method to be called when addFormComponent becomes available
  onAddFormComponentReady(): void {
    // If we have a pending material name, load the images now
    if (this.pendingMaterialName) {
      this.loadMaterialImages(this.pendingMaterialName);
      this.pendingMaterialName = null;
    }
  }

  // Called when component becomes active (e.g., when navigating to this form)
  onActivate(): void {
    // Refresh options when component becomes active
    this.materialDropdown.load({ reset: true });
    this.loadCategoryOptions();
    this.loadCategoryTreeOptions();
    this.brandDropdown.load({ reset: true });
    this.loadTaxOptions();
  }

  private loadCategoryOptions(): void {
    this.categoryService
      .getCategoryOptions({ statusFlag: true, size: 20 })
      .subscribe({
        next: (options) => {
          this.categoryOptions.set(options);
          this.refreshInheritedTaxDisplay();
        },
        error: (error) => {
          console.error('Error loading category options:', error);
          this.categoryOptions.set([]);
        },
      });
  }

  private loadCategoryTreeOptions(): void {
    this.categoryService
      .getCategoryTree({
        statusFlag: true,
        subFlag: undefined,
        removeNotSubFlag: false,
      })
      .subscribe({
        next: (treeOptions) => {
          // getCategoryTree() already returns TreeNode[] format, no need to convert again
          this.categoryTreeOptions.set(treeOptions);
          this.refreshInheritedTaxDisplay();
        },
        error: (error) => {
          console.error('Error loading category tree options:', error);
          this.categoryTreeOptions.set([]);
        },
      });
  }

  /**
   * Load serial profile options for dropdown
   */
  private loadSerialPolicyOptions(): void {
    this.serialProfileApiService.getAllSerialPolicy().subscribe({
      next: (policies) => {
        const options = policies.map((policy) => {
          const requirementKeys = [
            policy.lotRequired && 'lot',
            policy.expiryRequired && 'expiry',
            policy.serialRequired && 'serial',
          ].filter(Boolean) as string[];

          const requirements = requirementKeys.map((key) =>
            this.translateService.instant(
              `settings.serial_profile.requirements.${key}`,
            ),
          );

          const requirementsText =
            requirements.length > 0
              ? ` (${this.translateService.instant('settings.serial_profile.requirements.label')}: ${requirements.join(', ')})`
              : '';

          return {
            label: `${policy.name}${requirementsText}`,
            value: policy.id,
          };
        });
        this.serialProfileOptions.set(options);
        this.formConfigWithTreeOptions = null;
      },
      error: () => {
        this.serialProfileOptions.set([]);
        this.formConfigWithTreeOptions = null;
      },
    });
  }

  private loadTaxOptions(): void {
    this.taxCategoryService.getActiveOptions().subscribe({
      next: (options) => {
        this.taxOptions.set(options);
        this.formConfigWithTreeOptions = null;
      },
      error: () => {
        this.taxOptions.set([]);
        this.formConfigWithTreeOptions = null;
      },
    });
  }

  private loadProductFields(): void {
    this.goodsSkuControllerService
      .getMappingFields('body', false, { httpHeaderAccept: 'application/json' as any })
      .subscribe({
      next: (fields) => {
        this.productFields.set(fields);
        this.formConfigWithTreeOptions = null;
      },
      error: () => {
        this.productFields.set([]);
        this.formConfigWithTreeOptions = null;
      },
    });
  }

  private loadExistingData(): void {
    const currentSpuId = this.spuId();
    if (!currentSpuId) {
      return;
    }

    this.spuApiService.getSpuById(currentSpuId).subscribe({
      next: (apiItem: any) => {
        if (apiItem) {
          this.loadFormDataFromApi(apiItem);
          this.loadSerialProfileForSpu(currentSpuId);
          this.loadSpuImages(currentSpuId);
          if (this.showFaq) {
            this.loadSpuFaqs(currentSpuId);
          }
        } else {
          this.showErrorMessage('goods.spu.messages.error.product_not_found');
        }
      },
      error: () => {
        this.showErrorMessage(
          'goods.spu.messages.error.failed_to_load_products',
        );
      },
    });
  }

  /** RBA-2278: Load SPU images with their alt text metadata (edit/view mode only). */
  private loadSpuImages(spuId: number): void {
    this.spuApiService.getSpuImages(spuId).subscribe({
      next: (images) => {
        this.productImages.set(
          (images || []).map((image) => ({
            ...image,
            editedAltText: image.altText || '',
          })),
        );
      },
      error: (error) => {
        console.error('Error loading SPU image alt text:', error);
        this.productImages.set([]);
      },
    });
  }

  /** Edit mode only — nothing to load before the SPU exists. */
  private loadSpuFaqs(spuId: number): void {
    this.spuApiService.getSpuFaqs(spuId).subscribe({
      next: (faqs) => {
        const mapped = (faqs || []).map((faq) => this.toFaqEditItem(faq));
        this.faqItems.set(mapped);
        this.initialFaqCount = mapped.length;
        this.faqsLoaded = true;
      },
      error: (error) => {
        console.error('Error loading SPU FAQs:', error);
        this.faqsLoaded = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'goods.spu.form.faq.load_error',
          ),
        });
      },
    });
  }

  private toFaqEditItem(faq: SpuFaqVO): FaqEditItem {
    return {
      tempId: this.nextFaqTempId(),
      id: faq.id,
      questionVi: faq.questionVi || '',
      answerVi: faq.answerVi || '',
      questionEn: faq.questionEn || '',
      answerEn: faq.answerEn || '',
      sortOrder: faq.sortOrder ?? 0,
      active: faq.active ?? true,
    };
  }

  private nextFaqTempId(): string {
    this.faqTempIdCounter += 1;
    return `faq-${this.faqTempIdCounter}`;
  }

  private showErrorMessage(detailKey: string): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: this.translateService.instant(detailKey),
    });
  }

  private loadFormDataFromApi(apiItem: any): void {
    // Set flag to prevent formValueChanges from overwriting during initial load
    this.isInitialDataLoading = true;

    // Find the category object from tree options to get proper label
    let categoryValue: any = '';
    if (apiItem.categoryId || apiItem.categoryName) {
      const categoryOption = findCategoryOptionInTree(
        this.categoryTreeOptions(),
        apiItem.categoryId || apiItem.categoryName,
      );
      if (categoryOption) {
        // Use the full object with value and label for treeselect
        categoryValue = categoryOption;
      } else {
        // If category not found in options, create a temporary object
        categoryValue = {
          value: apiItem.categoryId?.toString() || '',
          label: apiItem.categoryName || `Category ${apiItem.categoryId}`,
        };
      }
    }

    // Load form data
    const formDataToSet = {
      combinationType:
        this.config.spuType === 'package'
          ? this.translateService.instant(
              'goods.spu.form.placeholders.package_product',
            )
          : this.translateService.instant(
              'goods.spu.form.placeholders.standard_product',
            ),
      name: apiItem.name || '',
      spuCode: apiItem.spuCode || '',
      type: apiItem.type || 1, // Use numeric type (1 = Physical, 2 = Virtual)
      categoryName: categoryValue, // Use the category object for treeselect
      additionalCategoryIds: this.buildAdditionalCategoryValues(apiItem),
      brandName: apiItem.brandName,
      brandId: apiItem.brandId,
      materialName: apiItem.materialName,
      remark: apiItem.remark || '',
      status: apiItem.status === 1,
    };
    this.formData.set(formDataToSet);
    this.taxValue.set({
      mode: deriveProductTaxMode(apiItem.taxId, apiItem.isPriceIncludeVAT),
      taxId: apiItem.taxId ?? null,
      isPriceIncludeVAT: apiItem.isPriceIncludeVAT ?? null,
    });
    this.refreshInheritedTaxDisplay();

    // Reset flag after a short delay to allow form to patch
    setTimeout(() => {
      this.isInitialDataLoading = false;
    }, 100);

    // Load image data from material if materialName is set
    if (apiItem.materialName) {
      // Load material images immediately if addFormComponent is available, otherwise store for later
      if (this.addFormComponent) {
        this.loadMaterialImages(apiItem.materialName);
      } else {
        this.pendingMaterialName = apiItem.materialName;
      }
    } else {
      // Clear image data if no material is set
      this.mainImageData.set(null);
      this.detailedImageData.set(null);
      this.usageVideoData.set(null);
    }

    // Load properties from API attributes - check multiple possible field names
    const attributesData =
      apiItem.attrLists || apiItem.attributes || apiItem.attrList || [];

    if (attributesData && attributesData.length > 0) {
      const properties = attributesData.map((attr: any) => {
        // Convert API data to PropertyFormData format
        const propertyFormData: PropertyFormData = {
          name: attr.attributeName || '',
          code: attr.code || '',
          type: this.mapTypeToDisplayType(attr.type),
          submissionsType: this.mapSubmissionsTypeToLabel(
            attr.submissionsType || 0,
          ),
          generateSku:
            attr.isSku === true &&
            attr.submissionsType !== SPU_SUBMISSIONS_TYPES.TEXT_BOX,
          remark: attr.remark || '',
          values:
            (attr.values || attr.valueList || attr.attributeValues || [])?.map(
              (val: any) => ({
                id: val.id || Date.now(),
                value: val.value || val.attributeValue || '',
                sort: val.sort || val.sortOrder || 0,
                asset: val.asset || val.imageUrl || '', // Use imageUrl as asset if asset is not available
                imageUrl: val.imageUrl || '', // Include imageUrl field
                isSaved: true,
                isEditing: false,
              }),
            ) || [],
        };

        // Use formatPropertyForTable to ensure proper formatting including propertyAsset
        const formattedProperty = this.formatPropertyForTable(
          propertyFormData,
          attr.id || Date.now(),
        );

        // Override originalData with the raw API data for proper editing
        formattedProperty.originalData = {
          name: attr.attributeName || '',
          code: attr.code || '',
          type: attr.type, // Keep numeric for form
          submissionsType: this.mapSubmissionsTypeToLabel(
            attr.submissionsType || 0,
          ), // Use translated label for form
          remark: attr.remark || '',
          values:
            (attr.values || attr.valueList || attr.attributeValues || [])?.map(
              (val: any) => ({
                id: val.id || Date.now(),
                value: val.value || val.attributeValue || '',
                sort: val.sort || val.sortOrder || 0,
                asset: val.asset || '',
                imageUrl: val.imageUrl || '',
              }),
            ) || [],
        };

        return formattedProperty;
      });

      this.propertyTableData.set(properties);
      this.nextPropertyId.set(
        Math.max(...properties.map((p: any) => p.id)) + 1,
      );
    }

    const selectedCategoryId = this.normalizeCategoryId(categoryValue);
    if (selectedCategoryId !== null) {
      this.loadCategoryAttributeTemplate(selectedCategoryId);
    }
  }

  /**
   * Build the treeselect control value for additional (secondary) categories
   * from the SPU detail response's `categories` list, excluding the primary
   * category which is already bound to the `categoryName` field.
   */
  private buildAdditionalCategoryValues(apiItem: any): any[] {
    const categories: any[] = Array.isArray(apiItem?.categories)
      ? apiItem.categories
      : [];
    const primaryId =
      apiItem?.categoryId != null ? String(apiItem.categoryId) : null;

    return categories
      .filter((category) => category?.id != null && String(category.id) !== primaryId)
      .map((category) => this.resolveCategoryTreeValue(category));
  }

  /** Resolve a category to its tree-select node, falling back to a synthetic node if not (yet) loaded. */
  private resolveCategoryTreeValue(category: { id: number; name?: string }): any {
    const found = findCategoryOptionInTree(this.categoryTreeOptions(), category.id);
    if (found) {
      return found;
    }
    return {
      key: String(category.id),
      value: String(category.id),
      label: category.name || `Category ${category.id}`,
    };
  }

  /**
   * Tree options for the `additionalCategoryIds` field, excluding whatever
   * category is currently selected as the primary `categoryName` so a user
   * can't pick the same category twice.
   */
  private additionalCategoryTreeOptions(): any[] {
    const primaryId = extractCategoryId(this.formData()?.categoryName);
    if (primaryId == null) {
      return this.categoryTreeOptions();
    }
    // Clone first: filterOutCurrentCategory mutates nested `children` arrays in
    // place, and categoryName's own treeOptions binding shares these node objects.
    const clonedOptions = this.cloneCategoryTreeOptions(
      this.categoryTreeOptions()
    );
    return filterOutCurrentCategory(clonedOptions, String(primaryId));
  }

  private cloneCategoryTreeOptions(options: TreeNode[]): TreeNode[] {
    return options.map((option) => ({
      key: option.key,
      label: option.label,
      value: option.value,
      children: option.children
        ? this.cloneCategoryTreeOptions(option.children)
        : [],
    }));
  }

  private mapTypeToDisplayType(type: number): string {
    return getTypeLabel(type, this.translateService);
  }

  private mapSubmissionsTypeToLabel(submissionsType: number): string {
    return getSubmissionsTypeLabel(submissionsType, this.translateService);
  }

  private loadCategoryAttributeTemplate(categoryId: number): void {
    if (!Number.isFinite(categoryId) || categoryId <= 0) {
      this.selectedCategoryTemplateId = null;
      this.removeCategoryTemplateRows();
      return;
    }

    this.selectedCategoryTemplateId = categoryId;
    this.categoryService.getCategoryAttributes(categoryId).subscribe({
      next: (attributes: CategoryAttributeVO[]) => {
        if (this.selectedCategoryTemplateId !== categoryId) {
          return;
        }
        this.applyCategoryTemplate(attributes || []);
      },
      error: () => {
        if (this.selectedCategoryTemplateId !== categoryId) {
          return;
        }
        this.removeCategoryTemplateRows();
      },
    });
  }

  private applyCategoryTemplate(attributes: CategoryAttributeVO[]): void {
    const existingRows = this.propertyTableData().filter(
      (row) => !row.isCategoryTemplate,
    );
    let nextId = this.nextPropertyId();
    const existingCodes = new Set(
      existingRows
        .map((row) => String(row.code || '').trim())
        .filter((code) => code.length > 0),
    );
    const templateRows = attributes
      .filter((attribute) => {
        const code = String(attribute.code || '').trim();
        return code.length === 0 || !existingCodes.has(code);
      })
      .map((attribute) => {
        const row = this.mapCategoryAttributeToPropertyRow(attribute, nextId);
        nextId += 1;
        return row;
      });

    this.propertyTableData.set([...existingRows, ...templateRows]);
    this.selectedProperties.set([]);
    this.nextPropertyId.set(nextId);
  }

  private mapCategoryAttributeToPropertyRow(
    attribute: CategoryAttributeVO,
    id: number,
  ): any {
    const sortedValues = [...(attribute.attributeValueList || [])].sort(
      (left, right) => (left.sort || 0) - (right.sort || 0),
    );
    const propertyFormData: PropertyFormData = {
      name: attribute.name || '',
      code: attribute.code || '',
      type: this.mapTypeToDisplayType(TYPES.CATEGORY_ATTRIBUTE),
      submissionsType: this.mapSubmissionsTypeToLabel(
        this.mapCategoryFormTypeToSubmissionsType(attribute.formType),
      ),
      generateSku:
        this.config.spuType !== 'package' &&
        attribute.skuFlag === true &&
        attribute.formType !== CATEGORY_FORM_TYPES.TEXT_BOX,
      remark: attribute.remark || '',
      values: sortedValues.map((value, index) => ({
        id: index + 1,
        value: value.value || '',
        sort: value.sort || index + 1,
        asset: '',
        imageUrl: '',
        isSaved: true,
        isEditing: false,
      })),
    };
    const row = this.formatPropertyForTable(propertyFormData, id);

    return {
      ...row,
      isCategoryTemplate: true,
      categoryTemplateCategoryId: attribute.categoryId,
      sourceType: attribute.sourceType,
    };
  }

  private mapCategoryFormTypeToSubmissionsType(formType?: number): number {
    switch (formType) {
      case CATEGORY_FORM_TYPES.CHECKBOX:
        return SPU_SUBMISSIONS_TYPES.CHECKBOX;
      case CATEGORY_FORM_TYPES.TEXT_BOX:
        return SPU_SUBMISSIONS_TYPES.TEXT_BOX;
      case CATEGORY_FORM_TYPES.DROPDOWN:
        return SPU_SUBMISSIONS_TYPES.DROPDOWN;
      case CATEGORY_FORM_TYPES.RADIO_BUTTON:
      default:
        return SPU_SUBMISSIONS_TYPES.RADIO_BUTTON;
    }
  }

  private removeCategoryTemplateRows(): void {
    this.propertyTableData.set(
      this.propertyTableData().filter((row) => !row.isCategoryTemplate),
    );
    this.selectedProperties.set([]);
  }

  private normalizeCategoryId(value: any): number | null {
    const categoryId = Number(extractCategoryId(value));
    return Number.isFinite(categoryId) && categoryId > 0 ? categoryId : null;
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'save') {
      if (this.isFormInvalid()) {
        this.markInvalidFormControls();
        const invalidFields = this.addFormComponent.getInvalidFieldNames();
        const fieldLabels = invalidFields.map((labelKey) =>
          this.translateService.instant(labelKey),
        );
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
                    'goods.spu.messages.error.required_fields_detail',
                    { fields: fieldLabels.join(', ') },
                  ),
        });
        return;
      }
      if (this.isSaving()) {
        return;
      }
      this.saveSpuData(event.data);
    } else if (event.action === 'cancel') {
      // Get the current query parameters from the route
      const currentQueryParams = this.route.snapshot.queryParams;

      // Remove the 'id' parameter when navigating back to list page
      const { id, ...filterParams } = currentQueryParams;

      // Navigate back to the list page with preserved filter parameters
      this.router.navigate([this.config.listRoute], {
        queryParams: filterParams,
      });
    }
  }

  private isFormInvalid(): boolean {
    const form = this.addFormComponent?.form;
    if (!form) {
      return true;
    }

    form.updateValueAndValidity({ emitEvent: false });
    return form.invalid;
  }

  private markInvalidFormControls(): void {
    const form = this.addFormComponent?.form;
    if (!form) {
      return;
    }

    form.markAllAsTouched();
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      if (control?.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
    this.addFormComponent?.markForCheck();
  }

  private saveSpuData(formData: any): void {
    // Set loading state to prevent multiple API calls
    this.isSaving.set(true);
    this.setSpuServiceType();

    try {
      const spuData = this.prepareSpuData(formData);
      const currentSpuId = this.spuId();

      if (this.editMode && currentSpuId) {
        this.updateSpu(spuData, formData.serialProfileId, currentSpuId);
      } else {
        this.createSpu(spuData, formData.serialProfileId);
      }
    } catch (error) {
      this.handleSaveError(error, 'save');
    }
  }

  private setSpuServiceType(): void {
    this.spuService.setSpuType(
      this.config.spuType === 'package' ? 'package' : 'ordinary',
    );
  }

  private prepareSpuData(formData: any): any {
    const currentFormData = this.formData();
    if (this.config.spuType !== 'package') {
      this.preserveBrandData(formData, currentFormData);
    }

    const processedProperties = this.processProperties();
    const categoryId = extractCategoryId(formData.categoryName);
    const additionalCategoryIds = this.extractAdditionalCategoryIds(
      formData.additionalCategoryIds,
      categoryId,
    );
    const brandId =
      this.config.spuType === 'package' ? undefined : this.resolveBrandId(formData);

    return {
      ...formData,
      categoryName: formData.categoryName?.label || '',
      categoryId,
      additionalCategoryIds,
      brandName: this.config.spuType === 'package' ? undefined : formData.brandName,
      brandId,
      serialProfileId: formData.serialProfileId || undefined,
      taxOverrideMode: this.taxValue().mode,
      ...normalizeProductTaxSubmit(this.taxValue()),
      properties: processedProperties,
    };
  }

  /** Extract additional category ids from the treeselect value, deduplicated against the primary category. */
  private extractAdditionalCategoryIds(
    value: any,
    primaryCategoryId: string | number | null,
  ): number[] {
    const extracted = extractCategoryIdFromTreeselect(value);
    const rawIds = Array.isArray(extracted) ? extracted : extracted ? [extracted] : [];
    const primaryId =
      primaryCategoryId != null ? Number(primaryCategoryId) : null;

    return rawIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id !== primaryId);
  }

  private preserveBrandData(formData: any, currentFormData: any): void {
    if (currentFormData?.brandId && !formData.brandId) {
      formData.brandId = currentFormData.brandId;
    }
    if (currentFormData?.brandName && !formData.brandName) {
      formData.brandName = currentFormData.brandName;
    }
  }

  private processProperties(): any[] {
    return this.propertyTableData().map((prop) => {
      const values =
        prop.valuesArray || (Array.isArray(prop.values) ? prop.values : []);
      const generateSku =
        this.config.spuType !== 'package'
          ? !this.isTextBoxProperty(prop) &&
            (prop.generateSku === 'Yes' || prop.generateSku === true)
          : false;

      return {
        ...prop,
        values,
        generateSku,
        remark: prop.remark === '-' ? '' : prop.remark,
        code: prop.code === '-' ? '' : prop.code,
      };
    });
  }

  private resolveBrandId(formData: any): number | undefined {
    if (
      formData.brandName === null ||
      formData.brandName === undefined ||
      formData.brandName === ''
    ) {
      return undefined;
    }
    if (formData.brandId) {
      return formData.brandId;
    }
    const brand = this.brandService.getBrandByName(formData.brandName);
    return brand ? brand.id : undefined;
  }

  private normalizeOptionalNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private isTextBoxProperty(prop: any): boolean {
    return (
      prop?.submissionsType ===
      this.mapSubmissionsTypeToLabel(SPU_SUBMISSIONS_TYPES.TEXT_BOX)
    );
  }

  private updateSpu(
    spuData: any,
    newSerialProfileId: number | undefined,
    spuId: number,
  ): void {
    spuData.id = spuId;
    const serialProfileChanged =
      newSerialProfileId !== this.originalSerialProfileId;

    this.spuService.updateSpu(spuData).subscribe({
      next: () => {
        this.saveImageAltTextIfNeeded(spuId);
        this.saveFaqsIfNeeded(spuId).then(() => {
          if (serialProfileChanged && newSerialProfileId) {
            this.reassignSerialProfileAndSave(
              newSerialProfileId,
              spuId,
              'update',
            );
          } else {
            this.handleSaveSuccess('update');
          }
        });
      },
      error: (error: HttpErrorResponse) => {
        this.handleSaveError(error, 'update');
      },
    });
  }

  /**
   * RBA-2278: Persist image alt text edits after a successful SPU update.
   * Only called in edit mode (an SPU id is required); create mode is handled
   * entirely server-side. Failures are surfaced via a separate toast and do
   * not block/rollback the main SPU save.
   */
  private saveImageAltTextIfNeeded(spuId: number): void {
    const items: SpuImageAltUpdateDTO[] = this.productImages()
      .filter((image) => !!image.objectName && !!image.role)
      .map((image) => ({
        objectName: image.objectName!,
        role: image.role as SpuImageAltUpdateDTO['role'],
        altText: image.editedAltText ?? '',
      }));

    if (items.length === 0) {
      return;
    }

    this.isSavingImageAlt.set(true);
    this.spuApiService.updateSpuImageAlt(spuId, items).subscribe({
      next: () => {
        this.isSavingImageAlt.set(false);
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'goods.spu.form.image_alt.save_success',
          ),
        });
      },
      error: (error) => {
        this.isSavingImageAlt.set(false);
        console.error('Error saving SPU image alt text:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'goods.spu.form.image_alt.save_error',
          ),
        });
      },
    });
  }

  private createSpu(
    spuData: any,
    newSerialProfileId: number | undefined,
  ): void {
    this.spuService.createSpu(spuData).subscribe({
      next: (result) => {
        const needsCreatedSpuId =
          !!newSerialProfileId || this.faqItems().length > 0;
        if (needsCreatedSpuId) {
          this.resolveCreatedSpuId(result)
            .then(async (createdSpuId) => {
              if (createdSpuId) {
                await this.saveFaqsIfNeeded(createdSpuId);
                if (newSerialProfileId) {
                  this.reassignSerialProfileAndSave(
                    newSerialProfileId,
                    createdSpuId,
                    'create',
                  );
                  return;
                }
              }
              this.handleSaveSuccess('create');
            })
            .catch(() => {
              // SPU created but failed to parse response, show success anyway
              this.handleSaveSuccess('create');
            });
        } else {
          this.handleSaveSuccess('create');
        }
      },
      error: (error: HttpErrorResponse) => {
        this.handleSaveError(error, 'create');
      },
    });
  }

  /**
   * Persist the current FAQ list as a full replace-set once a spuId is known
   * — both create (spuId just became available) and edit flows. Callers await
   * this before showing the single overall save toast, so FAQ save/validation
   * errors surface (their own toast) before that success toast would otherwise
   * fire — but never block/rollback the main SPU save.
   */
  private async saveFaqsIfNeeded(spuId: number): Promise<void> {
    if (!this.showFaq) {
      return;
    }

    const items = this.faqItems();

    // Create mode: nothing to persist if the user never added a row.
    if (items.length === 0 && !this.editMode) {
      return;
    }
    // Edit mode: a failed load must never be sent as "delete everything".
    if (this.editMode && !this.faqsLoaded) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'goods.spu.form.faq.load_error',
        ),
      });
      return;
    }
    // Nothing loaded and nothing added — skip the no-op PUT.
    if (items.length === 0 && this.initialFaqCount === 0) {
      return;
    }
    if (this.hasInvalidFaqItem(items)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'goods.spu.form.faq.validation.required',
        ),
      });
      return;
    }

    const payload: SpuFaqUpsertDTO[] = items.map((item, index) =>
      this.toFaqUpsertDto(item, index),
    );

    try {
      await firstValueFrom(this.spuApiService.updateSpuFaqs(spuId, payload));
      this.initialFaqCount = items.length;
    } catch (error) {
      console.error('Error saving SPU FAQs:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'goods.spu.form.faq.save_error',
        ),
      });
    }
  }

  private hasInvalidFaqItem(items: FaqEditItem[]): boolean {
    return items.some(
      (item) => !item.questionVi?.trim() || !item.answerVi?.trim(),
    );
  }

  private toFaqUpsertDto(item: FaqEditItem, index: number): SpuFaqUpsertDTO {
    return {
      id: item.id ?? null,
      questionVi: item.questionVi,
      answerVi: item.answerVi,
      questionEn: item.questionEn || null,
      answerEn: item.answerEn || null,
      sortOrder: index,
      active: item.active,
    };
  }

  private resolveCreatedSpuId(result: any): Promise<number | undefined> {
    return BlobConverterUtil.convertToJson(result?.response ?? result)
      .then((response) => this.extractSpuId(response))
      .catch(() => this.extractSpuId(result?.response ?? result));
  }

  private extractSpuId(response: any): number | undefined {
    if (response === null || response === undefined || response instanceof Blob) {
      return undefined;
    }

    if (typeof response === 'number') {
      return response;
    }

    if (typeof response === 'string') {
      const numericId = Number(response);
      if (Number.isFinite(numericId) && numericId > 0) {
        return numericId;
      }

      try {
        return this.extractSpuId(JSON.parse(response));
      } catch {
        return undefined;
      }
    }

    const candidates = [
      response.id,
      response.spuId,
      response.body?.id,
      response.body?.spuId,
      response.data?.id,
      response.data?.spuId,
      response.response?.id,
      response.response?.spuId,
    ];
    const resolvedId = candidates
      .map((value) => Number(value))
      .find((value) => Number.isFinite(value) && value > 0);

    return resolvedId;
  }

  private handleSaveError(
    error: any,
    mode: 'save' | 'create' | 'update',
  ): void {
    this.isSaving.set(false);

    if (error?.error?.errorCode === 'FIELD_PERMISSION_DENIED') {
      const deniedCodes: string[] = Array.isArray(error?.error?.data)
        ? error.error.data
        : [];
      const fieldLabels = deniedCodes
        .map(
          (code) =>
            this.productFields().find((field) => field.code === code)
              ?.label ?? code,
        )
        .join(', ');
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'goods.spu.messages.error.field_permission_denied',
          { fields: fieldLabels },
        ),
      });
      return;
    }

    if (error?.error?.errorCode === 'SPEC_EMBEDDED_IN_PRODUCT_TEXT') {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'goods.spu.messages.error.spec_embedded_in_product_text',
        ),
      });
      return;
    }

    const errorMessage = error?.error?.message || error?.message || '';
    const isDuplicateCode = errorMessage
      .toLowerCase()
      .includes('product with the same code exists');

    const detailKey =
      mode === 'create'
        ? isDuplicateCode
          ? 'goods.spu.messages.error.product_code_exists'
          : 'goods.spu.messages.error.failed_to_create_product'
        : isDuplicateCode
          ? 'goods.spu.messages.error.product_code_exists'
          : 'goods.spu.messages.error.failed_to_update_product';

    const detailMessage = isDuplicateCode
      ? this.translateService.instant(detailKey)
      : errorMessage || this.translateService.instant(detailKey);

    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: detailMessage,
    });
  }

  /**
   * Load serial profile for SPU and update form data
   */
  private loadSerialProfileForSpu(spuId: number): void {
    this.serialProfileApiService.getBySpuIds([spuId]).subscribe({
      next: (response) => {
        BlobConverterUtil.convertToJson(response).then((parsedResponse) => {
          const map = parsedResponse.spuProfileMap;
          const profileId =
            map?.[spuId] || map?.[String(spuId)] || map?.[spuId.toString()];

          if (profileId) {
            const currentFormData = this.formData();
            this.formData.set({
              ...currentFormData,
              serialProfileId: profileId,
            });
            this.originalSerialProfileId = profileId;
          }
        });
      },
    });
  }

  /**
   * Reassign serial profile and handle save success
   */
  private reassignSerialProfileAndSave(
    profileId: number,
    spuId: number,
    mode: 'create' | 'update',
  ): void {
    if (!spuId) {
      this.handleSaveSuccess(mode);
      return;
    }

    this.serialProfileApiService
      .reassignSpuToProfile(profileId, spuId)
      .subscribe({
        next: () => {
          this.handleSaveSuccess(mode);
        },
        error: () => {
          // Still show success for SPU save
          this.handleSaveSuccess(mode);
        },
      });
  }

  /**
   * Handle successful save operation
   */
  private handleSaveSuccess(mode: 'create' | 'update'): void {
    this.skipCanDeactivate = true;
    this.isSaving.set(false);
    this.hasManualChanges.set(false);
    this.addFormComponent?.form?.markAsPristine();
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.messages.success'),
      detail: this.translateService.instant(
        mode === 'create'
          ? 'goods.spu.messages.success.product_created'
          : 'goods.spu.messages.success.product_updated',
      ),
    });
    setTimeout(() => {
      const currentQueryParams = this.route.snapshot.queryParams;
      const { id, ...filterParams } = currentQueryParams;
      this.router.navigate([this.config.listRoute], {
        queryParams: filterParams,
      });
    }, 1000);
  }

  onTableAction(event: { section: string; action: string; data?: any }): void {
    if (event.action === 'selectionChange') {
      // Handle selection change
      this.selectedProperties.set(event.data || []);
    } else if (event.action === 'deleteSelected') {
      // Handle delete selected properties
      this.onDeleteSelectedProperties();
    } else if (event.action === 'importCategory') {
      // Handle import category
    } else if (event.action === 'addProperty') {
      this.editingProperty.set(null); // Reset editing property
      this.showAddPropertyDialog.set(true);
    }
  }

  onFormValueChanges(value: any): void {
    // Don't update formData during initial data loading to prevent overwriting API data
    if (this.isInitialDataLoading) {
      return;
    }

    // Only update if the value has actually changed to prevent unnecessary re-renders
    const currentData = this.formData();
    if (!this.hasFormDataChanged(currentData, value)) {
      return; // No changes, skip update
    }

    // Prevent overwriting meaningful data with empty values
    // If current data has meaningful values (like name, spuCode) and
    // incoming value has empty strings for those fields, ignore the update
    if (this.hasMeaningfulData(currentData) && !this.hasMeaningfulData(value)) {
      return;
    }

    const previousMaterialName = currentData?.materialName || '';
    const previousCategoryId = this.normalizeCategoryId(currentData?.categoryName);

    const updatedValue = { ...value };
    this.updateInheritedTaxValue(updatedValue.categoryName);

    // If brandName has changed, find the new brandId
    if (value.brandName && value.brandName !== currentData?.brandName) {
      // Brand name changed, find the new brandId from active brands
      const brand = this.brandService.getBrandByName(value.brandName);
      updatedValue.brandId = brand ? brand.id : undefined;
    } else if (
      value.brandName === null ||
      value.brandName === undefined ||
      value.brandName === ''
    ) {
      // Brand was explicitly cleared by user
      updatedValue.brandId = undefined;
    } else if (currentData?.brandId && !value.brandId) {
      // Only preserve brandId if brandName hasn't changed
      // This handles the case where the current brand is deactivated
      updatedValue.brandId = currentData.brandId;
    }

    const nextMaterialName = updatedValue.materialName || '';
    const hasMaterialChanged = previousMaterialName !== nextMaterialName;
    const nextCategoryId = this.normalizeCategoryId(updatedValue.categoryName);
    const hasCategoryChanged = previousCategoryId !== nextCategoryId;

    this.formData.set(updatedValue);

    if (hasCategoryChanged) {
      if (nextCategoryId !== null) {
        this.loadCategoryAttributeTemplate(nextCategoryId);
      } else {
        this.selectedCategoryTemplateId = null;
        this.removeCategoryTemplateRows();
      }
    }

    if (hasMaterialChanged) {
      // Handle material selection and load images from server
      if (nextMaterialName) {
        // Material was selected or changed
        this.loadMaterialImages(nextMaterialName);
        // Also update selected asset data immediately
        this.updateSelectedAssetData(nextMaterialName);
      } else {
        // Material was cleared
        this.mainImageData.set(null);
        this.detailedImageData.set(null);
        this.usageVideoData.set(null);
        this.selectedAssetData.set([]); // Clear selected asset data
        this.detailImagesData = []; // Clear non-signal property
      }
    }

    // Handle image uploads (only if not from material selection and if they are file uploads)
    if (
      value.mainImage &&
      !this.mainImageData()?.fromMaterial &&
      value.mainImage.file
    ) {
      this.mainImageData.set(value.mainImage);
    }
    if (value.detailedImage) {
      // Check if it's from material (array) or user upload (single object)
      const isFromMaterial =
        Array.isArray(this.detailedImageData()) &&
        this.detailedImageData().some((img: any) => img.fromMaterial);

      if (!isFromMaterial && value.detailedImage.file) {
        this.detailedImageData.set(value.detailedImage);
      }
    }
  }

  /**
   * Check if data has meaningful values (not just empty strings, null, undefined)
   */
  private hasMeaningfulData(data: any): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }

    // Check key fields that should have values
    const keyFields = ['name', 'spuCode', 'spuName'];
    return keyFields.some((field) => {
      const value = data[field];
      return value !== null && value !== undefined && value !== '';
    });
  }

  private hasFormDataChanged(currentData: any, newData: any): boolean {
    // Compare specific fields instead of using JSON.stringify to avoid circular reference issues
    const fieldsToCompare = [
      'spuName',
      'spuCode',
      'materialName',
      'brandName',
      'categoryName',
      'remark',
    ];

    for (const field of fieldsToCompare) {
      if (currentData[field] !== newData[field]) {
        return true;
      }
    }

    // Compare categoryName by extracting the ID to avoid circular reference
    const currentCategoryId = this.extractCategoryId(currentData.categoryName);
    const newCategoryId = this.extractCategoryId(newData.categoryName);

    if (currentCategoryId !== newCategoryId) {
      return true;
    }

    // Compare additionalCategoryIds by extracting ids to avoid circular reference
    return (
      this.stringifyAdditionalCategoryIds(currentData.additionalCategoryIds) !==
      this.stringifyAdditionalCategoryIds(newData.additionalCategoryIds)
    );
  }

  /** Stable string representation of the additional-categories selection, for change comparison. */
  private stringifyAdditionalCategoryIds(value: any): string {
    const extracted = extractCategoryIdFromTreeselect(value);
    const ids = Array.isArray(extracted) ? extracted : extracted ? [extracted] : [];
    return ids.map(String).sort().join(',');
  }

  onTaxValueChange(value: ProductTaxValue): void {
    this.taxValue.set(value);
    this.hasManualChanges.set(true);
  }

  private refreshInheritedTaxDisplay(): void {
    const currentData = this.formData();
    if (!currentData || this.taxValue().mode === 'OVERRIDE') {
      return;
    }

    this.updateInheritedTaxValue(currentData.categoryName);
  }

  private updateInheritedTaxValue(categoryName: any): void {
    const categoryId = this.normalizeCategoryId(categoryName);
    const inheritedTaxId = this.getInheritedCategoryTaxId(
      categoryName,
    );
    this.inheritedTaxValue.set({
      taxId: inheritedTaxId,
      taxCode: null,
      taxRate: null,
      isPriceIncludeVAT: null,
      source: inheritedTaxId === null ? 'UNCONFIGURED' : 'CATEGORY',
    });

    if (inheritedTaxId === null && categoryId !== null) {
      this.loadInheritedTaxFromCategoryDetail(categoryId);
    }
  }

  private loadInheritedTaxFromCategoryDetail(categoryId: number): void {
    this.categoryService.getCategoryDetail(categoryId).subscribe({
      next: (category) => {
        const currentData = this.formData();
        const currentCategoryId = this.normalizeCategoryId(
          currentData?.categoryName,
        );
        if (
          !currentData ||
          this.taxValue().mode === 'OVERRIDE' ||
          currentCategoryId !== categoryId
        ) {
          return;
        }

        const inheritedTaxId = this.normalizeOptionalNumber(category?.taxId);
        this.inheritedTaxValue.set({
          taxId: inheritedTaxId,
          taxCode: category?.taxCode ?? null,
          taxRate: category?.taxRate ?? null,
          isPriceIncludeVAT: null,
          source: inheritedTaxId === null ? 'UNCONFIGURED' : 'CATEGORY',
        });
      },
    });
  }

  private getInheritedCategoryTaxId(categoryValue: any): number | null {
    const categoryId = this.normalizeCategoryId(categoryValue);
    if (categoryId === null) {
      return null;
    }

    const treeNode = this.findCategoryTreeNodeById(
      this.categoryTreeOptions(),
      categoryId,
    );
    const treeTaxId = this.normalizeOptionalNumber(treeNode?.data?.taxId);
    if (treeTaxId !== null) {
      return treeTaxId;
    }

    const categoryOption = this.categoryOptions().find(
      (option) => option.id === categoryId,
    );
    return this.normalizeOptionalNumber(categoryOption?.taxId);
  }

  private findCategoryTreeNodeById(
    options: TreeNode[],
    categoryId: number,
  ): TreeNode | null {
    for (const option of options) {
      if (Number(option.value) === categoryId || Number(option.key) === categoryId) {
        return option;
      }
      const child = option.children
        ? this.findCategoryTreeNodeById(option.children as TreeNode[], categoryId)
        : null;
      if (child) {
        return child;
      }
    }
    return null;
  }

  private extractCategoryId(categoryName: any): string | number | null {
    if (!categoryName) {
      return null;
    }

    // If it's an object with value property (from treeselect)
    if (typeof categoryName === 'object' && categoryName.value !== undefined) {
      return categoryName.value;
    }

    // If it's a string or number (from loading existing data)
    if (typeof categoryName === 'string' || typeof categoryName === 'number') {
      return categoryName;
    }

    return null;
  }

  private loadMaterialImages(materialName: string): void {
    // Clear previous image data first
    this.mainImageData.set(null);
    this.detailedImageData.set(null);
    this.usageVideoData.set(null);

    // First, ensure materials are loaded
    this.materialService
      .getMaterialOptions({ materialName, size: 1 })
      .subscribe({
        next: (options) => {
          // Now try to get the material images
          const materialImages =
            this.materialService.getMaterialImages(materialName);
          this.processMaterialImages(materialImages);
        },
        error: (error) => {
          console.error('Error loading material options:', error);
          // Still try to get images in case they're already cached
          const materialImages =
            this.materialService.getMaterialImages(materialName);
          this.processMaterialImages(materialImages);
        },
      });
  }

  private processMaterialImages(materialImages: any): void {
    if (materialImages.mainImage) {
      // Handle both single object and array formats for main image
      let processedMainImage: any;
      if (Array.isArray(materialImages.mainImage)) {
        processedMainImage = materialImages.mainImage.map((img: any) => ({
          ...img,
          fromMaterial: true, // Flag to indicate this is from material selection
        }));
      } else {
        processedMainImage = {
          ...materialImages.mainImage,
          fromMaterial: true, // Flag to indicate this is from material selection
        };
      }
      this.mainImageData.set(processedMainImage);

      // Update form control value and AddFormComponent signals if form is ready
      setTimeout(() => {
        if (this.addFormComponent?.form) {
          const mainImageControl = this.addFormComponent.form.get('mainImage');
          if (mainImageControl && !mainImageControl.disabled) {
            // Set form control value to ensure it updates
            // FileUploadComponent expects single object, not array
            const valueToSet = Array.isArray(processedMainImage)
              ? processedMainImage[0]
              : processedMainImage;
            mainImageControl.setValue(valueToSet || null, { emitEvent: false });
          }
          // Update AddFormComponent's main images signal
          this.addFormComponent.updateMainImagesFromMaterial(
            processedMainImage,
          );
          // Trigger change detection to refresh getExistingFiles()
          this.cdr.markForCheck();
        }
      }, 200);
    } else {
      // Clear main image if material has no main image
      this.mainImageData.set(null);
      setTimeout(() => {
        if (this.addFormComponent?.form) {
          const mainImageControl = this.addFormComponent.form.get('mainImage');
          if (mainImageControl && !mainImageControl.disabled) {
            mainImageControl.setValue(null, { emitEvent: false });
          }
          this.addFormComponent.updateMainImagesFromMaterial(null);
          // Trigger change detection to refresh getExistingFiles()
          this.cdr.markForCheck();
        }
      }, 200);
    }

    if (materialImages.detailImage) {
      // Handle both single object and array formats
      let processedDetailImages: any;
      if (Array.isArray(materialImages.detailImage)) {
        processedDetailImages = materialImages.detailImage.map((img: any) => ({
          ...img,
          fromMaterial: true, // Flag to indicate this is from material selection
        }));
      } else {
        processedDetailImages = {
          ...materialImages.detailImage,
          fromMaterial: true, // Flag to indicate this is from material selection
        };
      }
      this.detailedImageData.set(processedDetailImages);

      // Update form control value and AddFormComponent signals if form is ready
      setTimeout(() => {
        if (this.addFormComponent?.form) {
          const detailImagesControl =
            this.addFormComponent.form.get('detailedImage');
          if (detailImagesControl && !detailImagesControl.disabled) {
            // MultiFileUploadComponent expects array
            const valueToSet = Array.isArray(processedDetailImages)
              ? processedDetailImages
              : [processedDetailImages];
            detailImagesControl.setValue(valueToSet, { emitEvent: false });
          }
          // Update AddFormComponent's detailed images signal
          this.addFormComponent.updateDetailedImagesFromMaterial(
            processedDetailImages,
          );
          // Trigger change detection to refresh getExistingFiles()
          this.cdr.markForCheck();
        }
      }, 200);
    } else {
      // Clear detail images if material has no detail images
      this.detailedImageData.set(null);
      setTimeout(() => {
        if (this.addFormComponent?.form) {
          const detailImagesControl =
            this.addFormComponent.form.get('detailedImage');
          if (detailImagesControl && !detailImagesControl.disabled) {
            detailImagesControl.setValue(null, { emitEvent: false });
          }
          this.addFormComponent.updateDetailedImagesFromMaterial(null);
          // Trigger change detection to refresh getExistingFiles()
          this.cdr.markForCheck();
        }
      }, 200);
    }

    // Load usage-instruction video from material data (self-hosted only)
    if (materialImages.usageVideo) {
      this.usageVideoData.set(materialImages.usageVideo);
      setTimeout(() => {
        if (this.addFormComponent?.form) {
          const usageVideoControl = this.addFormComponent.form.get('usageVideo');
          if (usageVideoControl && !usageVideoControl.disabled) {
            usageVideoControl.setValue(materialImages.usageVideo, {
              emitEvent: false,
            });
          }
          this.cdr.markForCheck();
        }
      }, 200);
    } else {
      this.usageVideoData.set(null);
      setTimeout(() => {
        if (this.addFormComponent?.form) {
          const usageVideoControl = this.addFormComponent.form.get('usageVideo');
          if (usageVideoControl && !usageVideoControl.disabled) {
            usageVideoControl.setValue(null, { emitEvent: false });
          }
          this.cdr.markForCheck();
        }
      }, 200);
    }

    // Update selected asset data after loading material images
    const currentMaterialName = this.formData()?.materialName;
    if (currentMaterialName) {
      this.updateSelectedAssetData(currentMaterialName);
    }
  }

  onPropertyDialogConfirm(propertyData: PropertyFormData): void {
    this.hasManualChanges.set(true);

    if (this.editingProperty()) {
      // Update existing property
      const currentData = this.propertyTableData();
      const updatedData = currentData.map((item) =>
        item.id === this.editingProperty().id
          ? this.formatPropertyForTable(propertyData, item.id)
          : item,
      );
      this.propertyTableData.set(updatedData);
    } else {
      // Add new property
      const newProperty = this.formatPropertyForTable(
        propertyData,
        this.nextPropertyId(),
      );
      this.propertyTableData.set([...this.propertyTableData(), newProperty]);
      this.nextPropertyId.update((id) => id + 1);
    }

    this.showAddPropertyDialog.set(false);
    this.editingProperty.set(null);
  }

  // Method to refresh selected asset data when dialog opens
  onPropertyDialogOpen(): void {
    const currentMaterialName = this.formData()?.materialName;
    if (currentMaterialName) {
      this.updateSelectedAssetData(currentMaterialName);
    }
  }

  // Handle dialog visibility changes
  onPropertyDialogVisibilityChange(isVisible: boolean): void {
    if (isVisible) {
      this.onPropertyDialogOpen();
    }
  }

  onDeleteProperty(id: number): void {
    this.hasManualChanges.set(true);

    const currentData = this.propertyTableData();
    const filteredData = currentData.filter((item) => item.id !== id);
    this.propertyTableData.set(filteredData);
  }

  onDeleteSelectedProperties(): void {
    const selectedCount = this.selectedProperties().length;

    if (selectedCount === 0) {
      return;
    }

    this.confirmationMessage.set(
      this.translateService.instant(
        'goods.spu.messages.confirmation.delete_properties',
        { count: selectedCount },
      )
    );
    this.confirmationButtons.set([
      { label: 'common.actions.cancel', action: 'cancel', outlined: true },
      { label: 'common.actions.delete', action: 'confirm', severity: 'danger' },
    ]);
    this.showConfirmationDialog.set(true);
  }

  onConfirmationDialogAction(action: string): void {
    this.showConfirmationDialog.set(false);

    if (action === 'confirm') {
      this.hasManualChanges.set(true);

      // User confirmed deletion
      const selectedCount = this.selectedProperties().length;
      const currentData = this.propertyTableData();
      const selectedIds = this.selectedProperties().map((prop) => prop.id);
      const filteredData = currentData.filter(
        (item) => !selectedIds.includes(item.id),
      );
      this.propertyTableData.set(filteredData);
      this.selectedProperties.set([]); // Clear selection after deletion

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('common.messages.success'),
        detail: this.translateService.instant(
          'goods.spu.messages.success.properties_deleted',
        ),
      });
    }
    // If action is 'cancel', do nothing
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.isSaving()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty || this.hasManualChanges());
  }

  onEditProperty(id: number): void {
    // Find the property data and open dialog in edit mode
    const propertyItem = this.propertyTableData().find(
      (item) => item.id === id,
    );

    if (propertyItem) {
      const propertyData = {
        id: propertyItem.id,
        name: propertyItem.name,
        code: propertyItem.code === '-' ? '' : propertyItem.code,
        type: propertyItem.type, // Keep as string for form
        submissionsType: propertyItem.submissionsType, // Keep as string for form
        generateSku:
          this.config.spuType !== 'package' &&
          !this.isTextBoxProperty(propertyItem)
            ? propertyItem.generateSku
            : false,
        remark: propertyItem.remark === '-' ? '' : propertyItem.remark,
        values:
          propertyItem.valuesArray || propertyItem.originalData?.values || [],
      };

      this.editingProperty.set(propertyData);
      this.showAddPropertyDialog.set(true);
    }
  }

  onViewProperty(id: number): void {
    // Find the property data and open dialog in view mode
    const propertyItem = this.propertyTableData().find(
      (item) => item.id === id,
    );

    if (propertyItem) {
      const propertyData = {
        id: propertyItem.id,
        name: propertyItem.name,
        code: propertyItem.code === '-' ? '' : propertyItem.code,
        type: propertyItem.type,
        submissionsType: propertyItem.submissionsType,
        generateSku:
          this.config.spuType !== 'package' &&
          !this.isTextBoxProperty(propertyItem)
            ? propertyItem.generateSku
            : false,
        remark: propertyItem.remark === '-' ? '' : propertyItem.remark,
        values:
          propertyItem.valuesArray || propertyItem.originalData?.values || [],
      };

      this.viewingProperty.set(propertyData);
      this.showViewPropertyDialog.set(true);
    }
  }

  private formatPropertyForTable(
    propertyData: PropertyFormData,
    id: number,
  ): any {
    // Format property values for display - show actual values
    const valuesText =
      propertyData.values && propertyData.values.length > 0
        ? propertyData.values.map((v) => v.value).join(', ')
        : '';
    // Format property assets for display - extract assets from property values
    const propertyAssets = this.formatPropertyAssets(propertyData.values || []);

    const isPackage = this.config.spuType === 'package';

    const baseProperty = {
      id: id,
      name: propertyData.name,
      code: propertyData.code || '',
      type:
        typeof propertyData.type === 'number'
          ? this.mapTypeToDisplayType(propertyData.type)
          : propertyData.type, // Convert number to translated string if needed
      submissionsType: propertyData.submissionsType, // Already a string
      values: valuesText, // Show actual property values as string
      valuesArray: propertyData.values || [], // This is the actual array for API
      propertyAsset: propertyAssets, // Add property asset information
      remark: propertyData.remark || '',
      // Store original data for editing
      originalData: propertyData,
    };

    // Only add generateSku field for non-package SPUs
    if (!isPackage) {
      return {
        ...baseProperty,
        generateSku: propertyData.generateSku, // Keep as boolean for custom template
      };
    }

    return baseProperty;
  }

  // Format property assets for display in the main table
  private formatPropertyAssets(values: any[]): any[] {
    if (!values || values.length === 0) {
      return [];
    }

    // Get selected asset data once to avoid repeated signal calls
    const selectedAssets = this.selectedAssetData();

    // Extract assets from property values that have asset information OR imageUrl
    const assets = values
      .filter((value) => {
        return (
          (value.asset && value.asset.trim() !== '') ||
          (value.imageUrl && value.imageUrl.trim() !== '')
        );
      })
      .map((value) => {
        // If we have imageUrl from API response, use it directly
        if (value.imageUrl && value.imageUrl.trim() !== '') {
          return {
            value: value.value,
            asset: value.imageUrl, // Use imageUrl as asset identifier
            image: value.imageUrl, // Use imageUrl directly for display
            originalName: this.extractFileNameFromUrl(value.imageUrl),
            label: this.extractFileNameFromUrl(value.imageUrl),
          };
        }

        // Otherwise, find the corresponding asset info from selectedAssetData
        const assetInfo = this.findAssetInfoFromArray(
          value.asset,
          selectedAssets,
        );

        return {
          value: value.value,
          asset: value.asset,
          image: assetInfo?.src || assetInfo?.thumbnailSrc || '',
          originalName: assetInfo?.title || value.asset,
          label: assetInfo?.title || value.asset,
        };
      });

    return assets;
  }

  // Extract filename from URL for display
  private extractFileNameFromUrl(url: string): string {
    try {
      const pathname = new URL(url).pathname;
      const fileName = pathname.split('/').pop() || 'Unknown';
      return fileName;
    } catch {
      // If URL parsing fails, try to extract from the string
      const parts = url.split('/');
      return parts[parts.length - 1] || 'Unknown';
    }
  }

  // Find asset information from selectedAssetData based on asset value
  private findAssetInfo(assetValue: string): any {
    const selectedAssets = this.selectedAssetData();
    return this.findAssetInfoFromArray(assetValue, selectedAssets);
  }

  // Find asset information from a provided array (to avoid repeated signal calls)
  private findAssetInfoFromArray(
    assetValue: string,
    selectedAssets: any[],
  ): any {
    if (!selectedAssets || selectedAssets.length === 0) {
      return null;
    }

    // Find the asset that matches the value
    return selectedAssets.find((asset) => {
      return asset.src === assetValue;
    });
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }

  // RBA-2278: Update the local draft alt text for a given image (not yet saved).
  onAltTextChange(objectName: string | undefined, value: string): void {
    if (!objectName) return;
    this.productImages.update((images) =>
      images.map((image) =>
        image.objectName === objectName
          ? { ...image, editedAltText: value }
          : image,
      ),
    );
  }

  // RBA-2278: Reset a MANUAL image's alt text to the server-provided defaultAltText.
  onResetAltTextToDefault(objectName: string | undefined): void {
    if (!objectName) return;
    this.productImages.update((images) =>
      images.map((image) =>
        image.objectName === objectName
          ? {
              ...image,
              editedAltText: image.defaultAltText ?? image.altText ?? '',
            }
          : image,
      ),
    );
  }

  onFaqItemsChange(items: FaqEditItem[]): void {
    this.faqItems.set(items);
  }

  // Get display-only state for specific field
  isFieldDisplayOnly(fieldName: string): boolean {
    if (
      (fieldName === 'mainImage' || fieldName === 'detailedImage') &&
      this.formData()?.materialName
    ) {
      return true;
    }
    return false;
  }

  // Update selected asset data when product asset name changes
  private updateSelectedAssetData(productAssetName: string): void {
    if (!productAssetName) {
      this.selectedAssetData.set([]);
      this.detailImagesData = []; // Clear non-signal property
      return;
    }

    // Check if we already have the same data to prevent unnecessary updates
    const currentAssetData = this.selectedAssetData();
    if (currentAssetData.length > 0) {
      const currentProductName = currentAssetData[0]?.productName;
      if (currentProductName === productAssetName) {
        return;
      }
    }

    // Get the detail images from the current detailed image data
    const detailedImages = this.detailedImageData();

    if (!detailedImages) {
      this.selectedAssetData.set([]);
      this.detailImagesData = []; // Clear non-signal property
      return;
    }

    // Convert to the format expected by the dialog
    let assetData: any[] = [];
    if (Array.isArray(detailedImages)) {
      assetData = detailedImages.map((img: any) => ({
        id: img.id || img.fileName,
        src: img.previewUrl || img.src,
        title: img.fileName || img.title,
        thumbnailSrc: img.previewUrl || img.src,
        fileSize: img.fileSize,
      }));
    } else {
      // Single image object
      assetData = [
        {
          id: detailedImages.id || detailedImages.fileName,
          src: detailedImages.previewUrl || detailedImages.src,
          title: detailedImages.fileName || detailedImages.title,
          thumbnailSrc: detailedImages.previewUrl || detailedImages.src,
          fileSize: detailedImages.fileSize,
        },
      ];
    }

    this.selectedAssetData.set(assetData);
    this.detailImagesData = assetData; // Set non-signal property
  }

  // Get detail images for the property dialog
  getDetailImagesForDialog(): any[] {
    const detailedImages = this.detailedImageData();
    if (!detailedImages) return [];

    // Convert to the format expected by the dialog
    if (Array.isArray(detailedImages)) {
      return detailedImages.map((img: any) => ({
        id: img.id || img.fileName,
        src: img.previewUrl || img.src,
        title: img.fileName || img.title,
        thumbnailSrc: img.previewUrl || img.src,
        fileSize: img.fileSize,
      }));
    } else {
      // Single image object
      return [
        {
          id: detailedImages.id || detailedImages.fileName,
          src: detailedImages.previewUrl || detailedImages.src,
          title: detailedImages.fileName || detailedImages.title,
          thumbnailSrc: detailedImages.previewUrl || detailedImages.src,
          fileSize: detailedImages.fileSize,
        },
      ];
    }
  }
}
